import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const initialForm = {
  nombre: "",
  email: "",
  telefono: "",
  empresa: "",
  tipo: "",
  mensaje: "",
};

const contactInfo = [
  {
    icon: Phone,
    label: "Teléfono",
    value: "+56 9 9138 8500",
    href: "tel:+56991388500",
    color: "#00E0FF",
  },
  {
    icon: Mail,
    label: "Email",
    value: "Contacto@Edimedia.cl",
    href: "mailto:Contacto@Edimedia.cl",
    color: "#D4AF37",
  },
  {
    icon: MapPin,
    label: "Dirección",
    value: "Av Las Condes 7700, Santiago, Chile",
    href: "https://maps.google.com/?q=Av+Las+Condes+7700,+Santiago,+Chile",
    color: "#00E0FF",
  },
];

const Contacto = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.mensaje) {
      toast.error("Por favor completa los campos requeridos.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`/contact.php`, form);
      setSubmitted(true);
      setForm(initialForm);
      toast.success("Mensaje enviado. Nos pondremos en contacto pronto.");
    } catch (err) {
      toast.error("Error al enviar. Intenta nuevamente o escríbenos al email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contacto-section"
      style={{
        backgroundColor: "#050505",
        paddingTop: "120px",
        paddingBottom: "120px",
        borderTop: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <div className="edi-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left: Info */}
          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7 }}
            >
              <span className="section-label">Hablemos</span>
              <div style={{ marginTop: "20px" }}>
                <div className="line-accent" />
              </div>
              <h2
                className="section-heading mb-6"
                style={{ fontSize: "clamp(42px, 5vw, 68px)" }}
              >
                Contacto
              </h2>
              <p
                className="font-body text-zinc-400 mb-12"
                style={{ fontSize: "14px", lineHeight: 1.8, fontWeight: 300 }}
              >
                ¿Quieres publicitar tu marca o monetizar tu edificio? Cuéntanos sobre tu proyecto y te contactamos a la brevedad.
              </p>
            </motion.div>

            {/* Contact Items */}
            <div className="flex flex-col gap-8">
              {contactInfo.map((info, i) => {
                const Icon = info.icon;
                return (
                  <motion.a
                    key={i}
                    href={info.href}
                    target={info.href.startsWith("http") ? "_blank" : undefined}
                    rel={info.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    data-testid={`contact-${info.label.toLowerCase()}`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, delay: i * 0.1 }}
                    className="flex items-start gap-5 group"
                    style={{ textDecoration: "none" }}
                  >
                    <div
                      className="flex items-center justify-center flex-shrink-0"
                      style={{
                        width: "44px",
                        height: "44px",
                        border: `1px solid ${info.color}33`,
                        backgroundColor: `${info.color}08`,
                        transition: "border-color 0.3s, background-color 0.3s",
                      }}
                    >
                      <Icon size={17} style={{ color: info.color }} />
                    </div>
                    <div>
                      <span
                        className="font-body font-bold uppercase block"
                        style={{ fontSize: "10px", color: "#52525b", letterSpacing: "0.2em", marginBottom: "4px" }}
                      >
                        {info.label}
                      </span>
                      <span
                        className="font-body text-zinc-300 group-hover:text-white"
                        style={{ fontSize: "14px", fontWeight: 300, transition: "color 0.3s" }}
                      >
                        {info.value}
                      </span>
                    </div>
                  </motion.a>
                );
              })}
            </div>

            {/* Direct CTAs */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="flex flex-col gap-3 mt-12"
            >
              <a
                href="tel:+56991388500"
                data-testid="cta-phone-btn"
                className="flex items-center justify-between font-body font-bold uppercase text-xs"
                style={{
                  border: "1px solid rgba(0,224,255,0.25)",
                  color: "#00E0FF",
                  padding: "16px 20px",
                  letterSpacing: "0.15em",
                  transition: "background-color 0.3s",
                  textDecoration: "none",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(0,224,255,0.07)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <span>Llamar Ahora</span>
                <ArrowRight size={14} />
              </a>
              <a
                href="mailto:Contacto@Edimedia.cl"
                data-testid="cta-email-btn"
                className="flex items-center justify-between font-body font-bold uppercase text-xs"
                style={{
                  border: "1px solid rgba(212,175,55,0.25)",
                  color: "#D4AF37",
                  padding: "16px 20px",
                  letterSpacing: "0.15em",
                  transition: "background-color 0.3s",
                  textDecoration: "none",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(212,175,55,0.07)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <span>Enviar Email</span>
                <ArrowRight size={14} />
              </a>
              <a
                href="https://wa.me/56991388500"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp-btn"
                className="flex items-center justify-between font-body font-bold uppercase text-xs"
                style={{
                  border: "1px solid rgba(37,211,102,0.3)",
                  color: "#25D366",
                  padding: "16px 20px",
                  letterSpacing: "0.15em",
                  transition: "background-color 0.3s",
                  textDecoration: "none",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(37,211,102,0.07)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <span>WhatsApp</span>
                <ArrowRight size={14} />
              </a>
            </motion.div>
          </div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-8"
            style={{
              backgroundColor: "#0F0F10",
              border: "1px solid rgba(255,255,255,0.05)",
              padding: "48px",
            }}
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-16 gap-6">
                <div
                  style={{ width: "60px", height: "60px", border: "1px solid #00E0FF", display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <Send size={24} color="#00E0FF" />
                </div>
                <h3 className="font-heading font-bold uppercase text-white" style={{ fontSize: "28px" }}>
                  Mensaje Enviado
                </h3>
                <p className="font-body text-zinc-400 text-center" style={{ fontSize: "14px", lineHeight: 1.7, maxWidth: "360px" }}>
                  Gracias por contactarte. Nuestro equipo te responderá a la brevedad.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="font-body font-bold uppercase text-xs"
                  style={{ color: "#00E0FF", letterSpacing: "0.15em", border: "none", background: "none", cursor: "pointer" }}
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <label className="font-body font-bold uppercase text-zinc-600" style={{ fontSize: "10px", letterSpacing: "0.2em", display: "block", marginBottom: "8px" }}>
                      Nombre *
                    </label>
                    <input
                      name="nombre"
                      value={form.nombre}
                      onChange={handleChange}
                      placeholder="Tu nombre"
                      required
                      data-testid="input-nombre"
                      className="edi-input"
                    />
                  </div>
                  <div>
                    <label className="font-body font-bold uppercase text-zinc-600" style={{ fontSize: "10px", letterSpacing: "0.2em", display: "block", marginBottom: "8px" }}>
                      Email *
                    </label>
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="tu@email.com"
                      required
                      data-testid="input-email"
                      className="edi-input"
                    />
                  </div>
                  <div>
                    <label className="font-body font-bold uppercase text-zinc-600" style={{ fontSize: "10px", letterSpacing: "0.2em", display: "block", marginBottom: "8px" }}>
                      Teléfono
                    </label>
                    <input
                      name="telefono"
                      value={form.telefono}
                      onChange={handleChange}
                      placeholder="+56 9 ..."
                      data-testid="input-telefono"
                      className="edi-input"
                    />
                  </div>
                  <div>
                    <label className="font-body font-bold uppercase text-zinc-600" style={{ fontSize: "10px", letterSpacing: "0.2em", display: "block", marginBottom: "8px" }}>
                      Empresa / Edificio
                    </label>
                    <input
                      name="empresa"
                      value={form.empresa}
                      onChange={handleChange}
                      placeholder="Nombre de tu empresa o edificio"
                      data-testid="input-empresa"
                      className="edi-input"
                    />
                  </div>
                </div>

                <div className="mb-8">
                  <label className="font-body font-bold uppercase text-zinc-600" style={{ fontSize: "10px", letterSpacing: "0.2em", display: "block", marginBottom: "8px" }}>
                    Tipo de consulta
                  </label>
                  <select
                    name="tipo"
                    value={form.tipo}
                    onChange={handleChange}
                    data-testid="select-tipo"
                    className="edi-select"
                  >
                    <option value="">Selecciona una opción</option>
                    <option value="marca">Quiero Publicitar (Marca)</option>
                    <option value="comunidad">Quiero Monetizar mi Edificio (Comunidad)</option>
                    <option value="administrador">Soy Administrador de Edificios</option>
                    <option value="otro">Otra consulta</option>
                  </select>
                </div>

                <div className="mb-10">
                  <label className="font-body font-bold uppercase text-zinc-600" style={{ fontSize: "10px", letterSpacing: "0.2em", display: "block", marginBottom: "8px" }}>
                    Mensaje *
                  </label>
                  <textarea
                    name="mensaje"
                    value={form.mensaje}
                    onChange={handleChange}
                    placeholder="Cuéntanos sobre tu proyecto..."
                    required
                    rows={5}
                    data-testid="input-mensaje"
                    className="edi-input"
                    style={{ resize: "none" }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-contact-form"
                  className="flex items-center gap-3 font-body font-bold uppercase text-xs text-black"
                  style={{
                    backgroundColor: loading ? "#00a8bf" : "#00E0FF",
                    padding: "16px 36px",
                    letterSpacing: "0.18em",
                    border: "none",
                    cursor: loading ? "not-allowed" : "pointer",
                    transition: "background-color 0.3s",
                  }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = "#ffffff"; }}
                  onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = "#00E0FF"; }}
                >
                  <span>{loading ? "Enviando..." : "Enviar Mensaje"}</span>
                  {!loading && <Send size={13} />}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export { Contacto };
